import { AxiosInstance } from 'axios';
import { handleServiceError, ErrorResponse } from '@sdk/utils/error-handler';
import type {
  AutoTraderStatusResponse,
  RegisterAutoTraderRequest,
  RegisterAutoTraderResponse,
  AutoTraderApplicationFilters,
  AutoTraderApplicationListResponse,
  ApproveAutoTraderRequest,
  RejectAutoTraderRequest,
  AutoTraderActionResponse,
  AutoTraderStatsResponse,
  AutoTraderApplication,
} from '@sdk/shared/types/settings.types';
import { Auth } from '@sdk/auth';

/**
 * Unified AutoTrader Service handling both user and admin operations.
 * User methods use protectedAxios (user JWT).
 * Admin methods use adminAxios (admin JWT).
 */
export class AutoTraderService {
  private auth: Auth;

  constructor(auth: Auth) {
    this.auth = auth;
  }

  // ==========================================
  // USER ENDPOINTS (protectedAxios)
  // ==========================================

  /**
   * Get current user's auto-trader status and application details
   */
  getStatus = async (): Promise<AutoTraderStatusResponse> => {
    const { data } = await this.auth.protectedAxios.get<AutoTraderStatusResponse>(
      '/auto-trader/status',
    );
    if (!data) throw new Error('Failed to get auto-trader status');
    return data;
  };

  /**
   * Register as an auto-trader (requires KYC approval)
   */
  register = async (
    payload: RegisterAutoTraderRequest = {},
  ): Promise<RegisterAutoTraderResponse> => {
    const { data } = await this.auth.protectedAxios.post<RegisterAutoTraderResponse>(
      '/auto-trader/register',
      payload,
    );
    if (!data) throw new Error('Failed to register as auto-trader');
    return data;
  };

  /**
   * Get current user's application details (if exists)
   */
  getMyApplication = async (): Promise<AutoTraderApplication | null> => {
    const { data } = await this.auth.protectedAxios.get<AutoTraderApplication>(
      '/auto-trader/application',
    );
    return data;
  };

  // ==========================================
  // ADMIN ENDPOINTS (adminAxios)
  // ==========================================

  /**
   * Get list of auto-trader applications with filters (ADMIN)
   */
  getApplications = async (
    filters: AutoTraderApplicationFilters = {},
  ): Promise<ErrorResponse<AutoTraderApplicationListResponse>> => {
    return handleServiceError(async () => {
      const { data } = await this.auth.adminAxios.get<AutoTraderApplicationListResponse>(
        '/auto-trader/admin/applications',
        { params: filters },
      );
      if (!data) throw new Error('Failed to get auto-trader applications');
      return data;
    });
  };

  /**
   * Approve an auto-trader application (ADMIN)
   */
  approve = async (payload: ApproveAutoTraderRequest): Promise<AutoTraderActionResponse> => {
    const { data } = await this.auth.adminAxios.post<AutoTraderActionResponse>(
      '/auto-trader/admin/approve',
      payload,
    );
    if (!data) throw new Error('Failed to approve auto-trader');
    return data;
  };

  /**
   * Reject an auto-trader application with reason (ADMIN)
   */
  reject = async (payload: RejectAutoTraderRequest): Promise<AutoTraderActionResponse> => {
    const { data } = await this.auth.adminAxios.post<AutoTraderActionResponse>(
      '/auto-trader/admin/reject',
      payload,
    );
    if (!data) throw new Error('Failed to reject auto-trader');
    return data;
  };

  /**
   * Get auto-trader statistics (ADMIN)
   */
  getStats = async (): Promise<AutoTraderStatsResponse> => {
    const { data } = await this.auth.adminAxios.get<AutoTraderStatsResponse>(
      '/auto-trader/admin/stats',
    );
    if (!data) throw new Error('Failed to get auto-trader stats');
    return data;
  };
}
